import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Plus,
  Trash2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  RotateCcw,
  Download,
  Image as ImageIcon,
  Link as LinkIcon,
  Copy,
  Edit3,
  X,
  Upload,
  FileDown,
  Undo2,
  Redo2,
  Network,
  Sparkles,
  MousePointer,
  Check,
  FolderOpen,
  LayoutTemplate,
  Eraser,
  HelpCircle,
  ChevronDown,
  Loader2,
} from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { DashboardLayout } from '../components/DashboardLayout';
import { useData } from '../context/DataContext';
import { translations } from '../lib/i18n';
import {
  getSavedMindmaps,
  saveMindmaps,
  getActiveMindmapId,
  saveActiveMindmapId,
  getDefaultMindmapTemplates,
  createBlankMindmap,
} from '../lib/storage';
import type {
  MindmapDocument,
  MindmapNode,
  MindmapEdge,
  NodeShape,
  ArrowStyle,
  ArrowStroke,
  ArrowHead,
} from '../lib/types';

// Curated Clinical Palette Presets
export const CLINICAL_COLOR_PALETTES = [
  { id: 'blue', nameAr: 'أزرق سريري', nameFr: 'Bleu Clinique', hex: '#0284c7', text: '#ffffff', bg: '#f0f9ff', border: '#bae6fd' },
  { id: 'emerald', nameAr: 'أخضر تشخيصي', nameFr: 'Vert Diagnostic', hex: '#059669', text: '#ffffff', bg: '#ecfdf5', border: '#a7f3d0' },
  { id: 'red', nameAr: 'أحمر إسعافي', nameFr: 'Rouge Urgence', hex: '#dc2626', text: '#ffffff', bg: '#fef2f2', border: '#fecaca' },
  { id: 'amber', nameAr: 'برتقالي دوائي', nameFr: 'Ambre Pharmaco', hex: '#d97706', text: '#ffffff', bg: '#fffbeb', border: '#fde68a' },
  { id: 'purple', nameAr: 'بنفسجي جيني', nameFr: 'Violet Génétique', hex: '#7c3aed', text: '#ffffff', bg: '#faf5ff', border: '#ddd6fe' },
  { id: 'pink', nameAr: 'وردي أطفال', nameFr: 'Rose Pédiatrie', hex: '#db2777', text: '#ffffff', bg: '#fdf2f8', border: '#fbcfe8' },
  { id: 'slate', nameAr: 'رمادي تشريحي', nameFr: 'Gris Anatomie', hex: '#334155', text: '#ffffff', bg: '#f8fafc', border: '#cbd5e1' },
  { id: 'teal', nameAr: 'فيروزي وقائي', nameFr: 'Turquoise Prévention', hex: '#0d9488', text: '#ffffff', bg: '#f0fdfa', border: '#99f6e4' },
];

export const MindmapPage: React.FC = () => {
  const { language, theme } = useData();
  const t = translations[language];
  const isRtl = language === 'ar';

  // Mindmaps collection state
  const [mindmaps, setMindmaps] = useState<MindmapDocument[]>(() => {
    // If user previously opened mindmap with pre-seeded items, let's honor empty start preference if they only have default template
    const saved = getSavedMindmaps();
    if (saved.length === 1 && saved[0].id === 'template_chest_pain') {
      const blank = createBlankMindmap();
      saveMindmaps([blank]);
      saveActiveMindmapId(blank.id);
      return [blank];
    }
    return saved;
  });

  const [activeMapId, setActiveMapId] = useState<string>(() => {
    const savedMaps = getSavedMindmaps();
    if (savedMaps.length === 1 && savedMaps[0].id === 'template_chest_pain') {
      return mindmaps[0]?.id || 'mindmap_blank';
    }
    return getActiveMindmapId();
  });

  // Current active map
  const activeMap = useMemo(() => {
    return mindmaps.find((m) => m.id === activeMapId) || mindmaps[0] || createBlankMindmap();
  }, [mindmaps, activeMapId]);

  // Nodes & Edges of active map
  const [nodes, setNodes] = useState<MindmapNode[]>(activeMap.nodes || []);
  const [edges, setEdges] = useState<MindmapEdge[]>(activeMap.edges || []);
  const [mapTitle, setMapTitle] = useState<string>(activeMap.title || 'خريطة ذهنية جديدة');

  // Sync when activeMap changes
  useEffect(() => {
    setNodes(activeMap.nodes || []);
    setEdges(activeMap.edges || []);
    setMapTitle(activeMap.title || 'خريطة ذهنية جديدة');
  }, [activeMap.id]);

  // Save changes to mindmaps in localStorage (auto-save debounced)
  const autoSaveTimeoutRef = useRef<number | null>(null);
  const saveCurrentState = useCallback(
    (newNodes: MindmapNode[], newEdges: MindmapEdge[], newTitle: string) => {
      if (autoSaveTimeoutRef.current) {
        window.clearTimeout(autoSaveTimeoutRef.current);
      }
      autoSaveTimeoutRef.current = window.setTimeout(() => {
        setMindmaps((prev) => {
          const updated = prev.map((m) =>
            m.id === activeMapId
              ? { ...m, nodes: newNodes, edges: newEdges, title: newTitle, updatedAt: Date.now() }
              : m
          );
          saveMindmaps(updated);
          return updated;
        });
      }, 300);
    },
    [activeMapId]
  );

  // Undo / Redo History
  const [history, setHistory] = useState<{ nodes: MindmapNode[]; edges: MindmapEdge[] }[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);

  const pushHistory = useCallback(
    (newNodes: MindmapNode[], newEdges: MindmapEdge[]) => {
      setHistory((prev) => {
        const sliced = prev.slice(0, historyIndex + 1);
        return [...sliced, { nodes: newNodes, edges: newEdges }].slice(-25);
      });
      setHistoryIndex((prev) => Math.min(prev + 1, 24));
    },
    [historyIndex]
  );

  const handleUndo = () => {
    if (historyIndex > 0) {
      const prev = history[historyIndex - 1];
      setNodes(prev.nodes);
      setEdges(prev.edges);
      setHistoryIndex(historyIndex - 1);
      saveCurrentState(prev.nodes, prev.edges, mapTitle);
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      const next = history[historyIndex + 1];
      setNodes(next.nodes);
      setEdges(next.edges);
      setHistoryIndex(historyIndex + 1);
      saveCurrentState(next.nodes, next.edges, mapTitle);
    }
  };

  // Canvas Viewport Pan & Zoom
  const [zoom, setZoom] = useState<number>(1);
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Selection & Active tools
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [connectingSourceId, setConnectingSourceId] = useState<string | null>(null);
  const [editingNodeTitleId, setEditingNodeTitleId] = useState<string | null>(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<'png' | 'pdf' | null>(null);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState<boolean>(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setIsExportMenuOpen(false);
      }
    };
    if (isExportMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExportMenuOpen]);

  // Dragging node state
  const draggingNodeRef = useRef<{
    id: string;
    startX: number;
    startY: number;
    nodeInitX: number;
    nodeInitY: number;
  } | null>(null);

  // Selected node & edge objects
  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) || null,
    [nodes, selectedNodeId]
  );
  const selectedEdge = useMemo(
    () => edges.find((e) => e.id === selectedEdgeId) || null,
    [edges, selectedEdgeId]
  );

  // Delete Node (Completely deletes any node and its edges)
  const handleDeleteNode = useCallback(
    (id: string) => {
      setNodes((prevNodes) => {
        const nextNodes = prevNodes.filter((n) => n.id !== id);
        setEdges((prevEdges) => {
          const nextEdges = prevEdges.filter((e) => e.from !== id && e.to !== id);
          pushHistory(nextNodes, nextEdges);
          saveCurrentState(nextNodes, nextEdges, mapTitle);
          return nextEdges;
        });
        return nextNodes;
      });
      if (selectedNodeId === id) setSelectedNodeId(null);
    },
    [selectedNodeId, pushHistory, saveCurrentState, mapTitle]
  );

  // Delete Edge
  const handleDeleteEdge = useCallback(
    (id: string) => {
      setEdges((prevEdges) => {
        const nextEdges = prevEdges.filter((e) => e.id !== id);
        pushHistory(nodes, nextEdges);
        saveCurrentState(nodes, nextEdges, mapTitle);
        return nextEdges;
      });
      if (selectedEdgeId === id) setSelectedEdgeId(null);
    },
    [nodes, selectedEdgeId, pushHistory, saveCurrentState, mapTitle]
  );

  // Keyboard shortcut listener: Delete or Backspace to delete selected node or edge
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) {
        return;
      }
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedNodeId) {
          e.preventDefault();
          handleDeleteNode(selectedNodeId);
        } else if (selectedEdgeId) {
          e.preventDefault();
          handleDeleteEdge(selectedEdgeId);
        }
      }
      if (e.key === 'Escape') {
        setSelectedNodeId(null);
        setSelectedEdgeId(null);
        setConnectingSourceId(null);
        setEditingNodeTitleId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId, selectedEdgeId, handleDeleteNode, handleDeleteEdge]);

  // Clear Entire Canvas (Empty everything in current mindmap)
  const handleClearMindmap = () => {
    const confirmClear = window.confirm(
      language === 'ar'
        ? 'هل تريد مسح جميع العقد والأسهم والبدء من جديد بلوحة فارغة؟'
        : 'Voulez-vous effacer tous les nœuds et recommencer avec une carte vide ?'
    );
    if (!confirmClear) return;

    setNodes([]);
    setEdges([]);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
    setConnectingSourceId(null);
    pushHistory([], []);
    saveCurrentState([], [], mapTitle);
  };

  // Zoom handlers
  const handleZoom = (factor: number) => {
    setZoom((prev) => Math.max(0.25, Math.min(2.5, Number((prev * factor).toFixed(2)))));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleFitView = () => {
    if (nodes.length === 0 || !canvasContainerRef.current) {
      handleResetZoom();
      return;
    }
    const minX = Math.min(...nodes.map((n) => n.x));
    const maxX = Math.max(...nodes.map((n) => n.x + (n.width || 230)));
    const minY = Math.min(...nodes.map((n) => n.y));
    const maxY = Math.max(...nodes.map((n) => n.y + 120));

    const boxWidth = maxX - minX + 180;
    const boxHeight = maxY - minY + 180;

    const containerWidth = canvasContainerRef.current.clientWidth || 900;
    const containerHeight = canvasContainerRef.current.clientHeight || 600;

    const scaleX = containerWidth / boxWidth;
    const scaleY = containerHeight / boxHeight;
    const nextZoom = Math.max(0.35, Math.min(1.3, Math.min(scaleX, scaleY)));

    setZoom(nextZoom);
    setPan({
      x: containerWidth / 2 - ((minX + maxX) / 2) * nextZoom,
      y: containerHeight / 2 - ((minY + maxY) / 2) * nextZoom,
    });
  };

  // Wheel zoom and pan
  const handleWheel = (e: React.WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? 1.08 : 0.92;
      handleZoom(zoomFactor);
    } else {
      setPan((prev) => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY,
      }));
    }
  };

  // Canvas pointer down (Background pan or selection clear)
  const handleCanvasPointerDown = (e: React.MouseEvent) => {
    if (e.target === canvasContainerRef.current || (e.target as HTMLElement).tagName === 'svg') {
      if (connectingSourceId) {
        setConnectingSourceId(null);
      }
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
      setEditingNodeTitleId(null);

      // Start canvas panning
      setIsPanning(true);
      panStartRef.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
    }
  };

  const handleCanvasPointerMove = (e: React.MouseEvent) => {
    if (isPanning) {
      setPan({
        x: e.clientX - panStartRef.current.x,
        y: e.clientY - panStartRef.current.y,
      });
      return;
    }

    if (draggingNodeRef.current) {
      const { id, startX, startY, nodeInitX, nodeInitY } = draggingNodeRef.current;
      const dx = (e.clientX - startX) / zoom;
      const dy = (e.clientY - startY) / zoom;

      setNodes((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, x: Math.round(nodeInitX + dx), y: Math.round(nodeInitY + dy) } : n
        )
      );
    }
  };

  const handleCanvasPointerUp = () => {
    if (isPanning) {
      setIsPanning(false);
    }
    if (draggingNodeRef.current) {
      draggingNodeRef.current = null;
      pushHistory(nodes, edges);
      saveCurrentState(nodes, edges, mapTitle);
    }
  };

  // Double click anywhere on canvas background to add node right at cursor position
  const handleCanvasDoubleClick = (e: React.MouseEvent) => {
    if (e.target !== canvasContainerRef.current && (e.target as HTMLElement).tagName !== 'svg') {
      return;
    }

    const container = canvasContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clickX = (e.clientX - rect.left - pan.x) / zoom;
    const clickY = (e.clientY - rect.top - pan.y) / zoom;

    handleAddNodeAt(clickX - 110, clickY - 40);
  };

  // Add new node at specific coordinates or center
  const handleAddNodeAt = (posX?: number, posY?: number, shape: NodeShape = 'card') => {
    const container = canvasContainerRef.current;
    let targetX = posX;
    let targetY = posY;

    if (targetX === undefined || targetY === undefined) {
      const viewCenterX = container ? (container.clientWidth / 2 - pan.x) / zoom : 400;
      const viewCenterY = container ? (container.clientHeight / 2 - pan.y) / zoom : 300;
      const jitter = (Math.random() - 0.5) * 80;
      targetX = viewCenterX + jitter - 110;
      targetY = viewCenterY + jitter - 40;
    }

    const colorPreset = CLINICAL_COLOR_PALETTES[nodes.length % CLINICAL_COLOR_PALETTES.length];

    const newNode: MindmapNode = {
      id: `node_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      x: Math.round(targetX),
      y: Math.round(targetY),
      title: language === 'ar' ? 'عقدة جديدة' : 'Nouveau Nœud',
      subtitle: '',
      color: colorPreset.hex,
      textColor: colorPreset.text,
      shape: shape,
      width: shape === 'pill' ? 200 : shape === 'circle' ? 140 : 230,
    };

    const nextNodes = [...nodes, newNode];
    setNodes(nextNodes);
    setSelectedNodeId(newNode.id);
    setSelectedEdgeId(null);
    pushHistory(nextNodes, edges);
    saveCurrentState(nextNodes, edges, mapTitle);
  };

  // Node Drag Start
  const handleNodeDragStart = (e: React.MouseEvent, node: MindmapNode) => {
    e.stopPropagation();

    // If in connecting mode, connect to this target node
    if (connectingSourceId) {
      if (connectingSourceId !== node.id) {
        handleAddEdge(connectingSourceId, node.id);
      }
      setConnectingSourceId(null);
      return;
    }

    setSelectedNodeId(node.id);
    setSelectedEdgeId(null);

    draggingNodeRef.current = {
      id: node.id,
      startX: e.clientX,
      startY: e.clientY,
      nodeInitX: node.x,
      nodeInitY: node.y,
    };
  };

  // Add connected Child Node to the selected node
  const handleAddChildNode = (parentNode: MindmapNode) => {
    const colorPreset = CLINICAL_COLOR_PALETTES[(nodes.length + 1) % CLINICAL_COLOR_PALETTES.length];
    const childWidth = parentNode.shape === 'pill' ? 190 : 220;

    const childX = parentNode.x + (parentNode.width || 230) + 110;
    const childY = parentNode.y + (Math.random() - 0.5) * 60;

    const childNode: MindmapNode = {
      id: `node_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      x: Math.round(childX),
      y: Math.round(childY),
      title: language === 'ar' ? 'تفرع فرعي' : 'Sous-élément',
      subtitle: '',
      color: colorPreset.hex,
      textColor: colorPreset.text,
      shape: parentNode.shape,
      width: childWidth,
    };

    const newEdge: MindmapEdge = {
      id: `edge_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      from: parentNode.id,
      to: childNode.id,
      label: '',
      style: 'bezier',
      stroke: 'solid',
      arrowHead: 'arrow',
      color: parentNode.color,
      width: 2.5,
    };

    const nextNodes = [...nodes, childNode];
    const nextEdges = [...edges, newEdge];

    setNodes(nextNodes);
    setEdges(nextEdges);
    setSelectedNodeId(childNode.id);
    setSelectedEdgeId(null);
    pushHistory(nextNodes, nextEdges);
    saveCurrentState(nextNodes, nextEdges, mapTitle);
  };

  // Add Edge connection between two nodes
  const handleAddEdge = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    const exists = edges.some(
      (e) => (e.from === fromId && e.to === toId) || (e.from === toId && e.to === fromId)
    );
    if (exists) return;

    const sourceNode = nodes.find((n) => n.id === fromId);
    const newEdge: MindmapEdge = {
      id: `edge_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      from: fromId,
      to: toId,
      label: '',
      style: 'bezier',
      stroke: 'solid',
      arrowHead: 'arrow',
      color: sourceNode?.color || '#0284c7',
      width: 2.5,
    };

    const nextEdges = [...edges, newEdge];
    setEdges(nextEdges);
    setSelectedEdgeId(newEdge.id);
    pushHistory(nodes, nextEdges);
    saveCurrentState(nodes, nextEdges, mapTitle);
  };

  // Duplicate Node
  const handleDuplicateNode = (node: MindmapNode) => {
    const dupNode: MindmapNode = {
      ...node,
      id: `node_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      x: node.x + 40,
      y: node.y + 40,
      title: `${node.title} (نسخة)`,
    };
    const nextNodes = [...nodes, dupNode];
    setNodes(nextNodes);
    setSelectedNodeId(dupNode.id);
    pushHistory(nextNodes, edges);
    saveCurrentState(nextNodes, edges, mapTitle);
  };

  // Update Node properties
  const handleUpdateNode = (id: string, updates: Partial<MindmapNode>) => {
    const nextNodes = nodes.map((n) => (n.id === id ? { ...n, ...updates } : n));
    setNodes(nextNodes);
    saveCurrentState(nextNodes, edges, mapTitle);
  };

  // Update Edge properties
  const handleUpdateEdge = (id: string, updates: Partial<MindmapEdge>) => {
    const nextEdges = edges.map((e) => (e.id === id ? { ...e, ...updates } : e));
    setEdges(nextEdges);
    saveCurrentState(nodes, nextEdges, mapTitle);
  };

  // Image Upload handler for a node
  const handleNodeImageUpload = (nodeId: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (uploadEvent) => {
      const dataUrl = uploadEvent.target?.result as string;
      handleUpdateNode(nodeId, { imageUrl: dataUrl });
    };
    reader.readAsDataURL(file);
  };

  // Mindmap switching & management
  const handleSelectMap = (mapId: string) => {
    const map = mindmaps.find((m) => m.id === mapId);
    if (map) {
      setActiveMapId(mapId);
      saveActiveMindmapId(mapId);
      setNodes(map.nodes || []);
      setEdges(map.edges || []);
      setMapTitle(map.title || 'خريطة ذهنية');
      setSelectedNodeId(null);
      setSelectedEdgeId(null);
    }
  };

  const handleCreateNewBlankMap = () => {
    const newDoc = createBlankMindmap();
    const updated = [newDoc, ...mindmaps];
    setMindmaps(updated);
    saveMindmaps(updated);
    setActiveMapId(newDoc.id);
    saveActiveMindmapId(newDoc.id);
    setNodes([]);
    setEdges([]);
    setMapTitle(newDoc.title);
    setSelectedNodeId(null);
    setSelectedEdgeId(null);
  };

  // Load a pre-built template
  const handleLoadTemplate = (templateId: string) => {
    const templates = getDefaultMindmapTemplates();
    const chosen = templates.find((t) => t.id === templateId);
    if (!chosen) return;

    const newMapId = `mindmap_${Date.now()}`;
    const newDoc: MindmapDocument = {
      ...chosen,
      id: newMapId,
      updatedAt: Date.now(),
    };

    const updated = [newDoc, ...mindmaps];
    setMindmaps(updated);
    saveMindmaps(updated);
    setActiveMapId(newMapId);
    saveActiveMindmapId(newMapId);
    setNodes(newDoc.nodes);
    setEdges(newDoc.edges);
    setMapTitle(newDoc.title);
    setShowTemplatesModal(false);
  };

  // Export as PNG Image
  const handleExportPng = async () => {
    if (!canvasContainerRef.current) return;
    setExportLoading('png');
    try {
      const canvasEl = canvasContainerRef.current;
      const exportCanvas = await html2canvas(canvasEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: theme === 'dark' ? '#0f172a' : '#f8fafc',
      });

      const dataUrl = exportCanvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      a.download = `${mapTitle.replace(/[/\\?%*:|"<>]/g, '_')}_mindmap.png`;
      a.click();
    } catch (err) {
      console.error('PNG export failed', err);
    } finally {
      setExportLoading(null);
    }
  };

  // Export as PDF
  const handleExportPdf = async () => {
    if (!canvasContainerRef.current) return;
    setExportLoading('pdf');
    try {
      const canvasEl = canvasContainerRef.current;
      const exportCanvas = await html2canvas(canvasEl, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });

      const imgData = exportCanvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4',
      });

      pdf.addImage(imgData, 'JPEG', 10, 10, 277, 190);
      pdf.save(`${mapTitle.replace(/[/\\?%*:|"<>]/g, '_')}_mindmap.pdf`);
    } catch (err) {
      console.error('PDF export failed', err);
    } finally {
      setExportLoading(null);
    }
  };

  // JSON Export / Import
  const handleExportJson = () => {
    const jsonStr = JSON.stringify(activeMap, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mapTitle.replace(/[/\\?%*:|"<>]/g, '_')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target?.result as string);
        if (parsed && Array.isArray(parsed.nodes) && Array.isArray(parsed.edges)) {
          const importedMap: MindmapDocument = {
            id: `map_${Date.now()}`,
            title: parsed.title || 'Mindmap Importée',
            updatedAt: Date.now(),
            nodes: parsed.nodes,
            edges: parsed.edges,
          };
          const updated = [importedMap, ...mindmaps];
          setMindmaps(updated);
          saveMindmaps(updated);
          setActiveMapId(importedMap.id);
          saveActiveMindmapId(importedMap.id);
          setNodes(importedMap.nodes);
          setEdges(importedMap.edges);
          setMapTitle(importedMap.title);
        }
      } catch (err) {
        alert(language === 'ar' ? 'ملف JSON غير صالح' : 'Fichier JSON invalide');
      }
    };
    reader.readAsText(file);
  };

  // Edge Path Calculation Helper (Bézier, Straight, Stepped)
  const calculateEdgePath = (edge: MindmapEdge) => {
    const source = nodes.find((n) => n.id === edge.from);
    const target = nodes.find((n) => n.id === edge.to);
    if (!source || !target) return { path: '', midX: 0, midY: 0 };

    const sWidth = source.width || (source.shape === 'circle' ? 140 : 230);
    const sHeight = source.shape === 'circle' ? 140 : source.shape === 'pill' ? 56 : 100;
    const tWidth = target.width || (target.shape === 'circle' ? 140 : 230);
    const tHeight = target.shape === 'circle' ? 140 : target.shape === 'pill' ? 56 : 100;

    const sCenterX = source.x + sWidth / 2;
    const sCenterY = source.y + sHeight / 2;
    const tCenterX = target.x + tWidth / 2;
    const tCenterY = target.y + tHeight / 2;

    let sx = sCenterX;
    let sy = sCenterY;
    let tx = tCenterX;
    let ty = tCenterY;

    const dx = tCenterX - sCenterX;
    const dy = tCenterY - sCenterY;

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0) {
        sx = source.x + sWidth;
        tx = target.x;
      } else {
        sx = source.x;
        tx = target.x + tWidth;
      }
    } else {
      if (dy > 0) {
        sy = source.y + sHeight;
        ty = target.y;
      } else {
        sy = source.y;
        ty = target.y + tHeight;
      }
    }

    const midX = (sx + tx) / 2;
    const midY = (sy + ty) / 2;

    if (edge.style === 'straight') {
      return { path: `M ${sx} ${sy} L ${tx} ${ty}`, midX, midY };
    }

    if (edge.style === 'step') {
      const midStepX = sx + (tx - sx) / 2;
      return {
        path: `M ${sx} ${sy} L ${midStepX} ${sy} L ${midStepX} ${ty} L ${tx} ${ty}`,
        midX: midStepX,
        midY: (sy + ty) / 2,
      };
    }

    // Default: Smooth Cubic Bézier curve
    const curvature = Math.max(40, Math.min(180, Math.abs(tx - sx) * 0.5));
    const c1x = dx > 0 ? sx + curvature : sx - curvature;
    const c1y = sy;
    const c2x = dx > 0 ? tx - curvature : tx + curvature;
    const c2y = ty;

    return {
      path: `M ${sx} ${sy} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${tx} ${ty}`,
      midX,
      midY,
    };
  };

  return (
    <DashboardLayout>
      <div className="mindmap-studio-page" dir={isRtl ? 'rtl' : 'ltr'}>
        {/* ============================================================
            1. IMPROVED TOP TOOLBAR (Spacious, Elegant, Anti-Clutter)
            ============================================================ */}
        {/* ============================================================
            1. ELEVATED STUDIO TOPBAR (Executive, Clean, Anti-Clutter)
            ============================================================ */}
        <header className="mindmap-topbar">
          {/* Start Zone: Map Title, Switcher, and New Map */}
          <div className="mindmap-topbar-start">
            <div className="mindmap-title-chip" title="انقر لتعديل عنوان الخريطة">
              <div className="mindmap-brand-badge">
                <Network size={16} />
              </div>
              <input
                type="text"
                className="mindmap-title-input"
                value={mapTitle}
                onChange={(e) => {
                  setMapTitle(e.target.value);
                  saveCurrentState(nodes, edges, e.target.value);
                }}
                placeholder="عنوان الخريطة الذهنية..."
              />
              <Edit3 size={13} className="mindmap-title-edit-icon" />
            </div>

            {/* Mindmaps Switcher Dropdown */}
            <div className="mindmap-switcher-pill">
              <select
                className="mindmap-switcher-select"
                value={activeMapId}
                onChange={(e) => handleSelectMap(e.target.value)}
                title="التبديل بين الخرائط المحفوظة"
              >
                {mindmaps.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.title} ({m.nodes?.length || 0} عقد)
                  </option>
                ))}
              </select>
            </div>

            <button
              type="button"
              className="mindmap-new-map-btn"
              onClick={handleCreateNewBlankMap}
              title="إنشاء خريطة ذهنية جديدة فارغة"
            >
              <Plus size={14} />
              <span>{t.mindmapNew}</span>
            </button>
          </div>

          {/* Center Zone: Unified Floating Controls Capsule (History + Zoom + Fit) */}
          <div className="mindmap-topbar-center">
            <div className="mindmap-controls-capsule">
              <div className="capsule-btn-group">
                <button
                  type="button"
                  className="capsule-icon-btn"
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  title="تراجع (Ctrl+Z)"
                >
                  <Undo2 size={14} />
                </button>
                <button
                  type="button"
                  className="capsule-icon-btn"
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  title="إعادة (Ctrl+Y)"
                >
                  <Redo2 size={14} />
                </button>
              </div>

              <div className="capsule-divider" />

              <div className="capsule-zoom-group">
                <button
                  type="button"
                  className="capsule-icon-btn"
                  onClick={() => handleZoom(0.85)}
                  title="تصغير (-)"
                >
                  <ZoomOut size={13} />
                </button>
                <button
                  type="button"
                  className="capsule-zoom-val"
                  onClick={handleResetZoom}
                  title="إعادة التعيين إلى 100%"
                >
                  {Math.round(zoom * 100)}%
                </button>
                <button
                  type="button"
                  className="capsule-icon-btn"
                  onClick={() => handleZoom(1.15)}
                  title="تكبير (+)"
                >
                  <ZoomIn size={13} />
                </button>
              </div>

              <div className="capsule-divider" />

              <div className="capsule-btn-group">
                <button
                  type="button"
                  className="capsule-icon-btn"
                  onClick={handleFitView}
                  title="ملاءمة كامل الخريطة للشاشة"
                >
                  <Maximize2 size={13} />
                </button>
                <button
                  type="button"
                  className="capsule-icon-btn"
                  onClick={handleResetZoom}
                  title="إعادة ضبط الموضع والحجم"
                >
                  <RotateCcw size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* End Zone: Action Buttons (Add Node, Templates, Clear, Unified Export) */}
          <div className="mindmap-topbar-end">
            {/* Primary Action Button: Add Node */}
            <button
              type="button"
              className="mindmap-primary-cta"
              onClick={() => handleAddNodeAt()}
              title="إضافة عقدة جديدة للوحة"
            >
              <Plus size={15} />
              <span>{t.mindmapAddNode}</span>
            </button>

            {/* Templates Button */}
            <button
              type="button"
              className="mindmap-action-pill"
              onClick={() => setShowTemplatesModal(true)}
              title="عرض نماذج طبية جاهزة للاستلهام"
            >
              <LayoutTemplate size={14} />
              <span className="hidden lg:inline">{t.mindmapTemplates}</span>
            </button>

            {/* Clear Entire Map Button */}
            {nodes.length > 0 && (
              <button
                type="button"
                className="mindmap-action-pill text-danger-soft"
                onClick={handleClearMindmap}
                title="مسح جميع العقد والبدء بلوحة فارغة"
              >
                <Eraser size={14} />
                <span className="hidden xl:inline">مسح اللوحة</span>
              </button>
            )}

            {/* Unified Export & Save Dropdown */}
            <div className="mindmap-export-dropdown" ref={exportMenuRef}>
              <button
                type="button"
                className={`mindmap-export-trigger-btn ${isExportMenuOpen ? 'active' : ''}`}
                onClick={() => setIsExportMenuOpen((prev) => !prev)}
                title="تصدير وحفظ الخريطة الذهنية"
              >
                {exportLoading ? (
                  <Loader2 size={14} className="animate-spin text-primary" />
                ) : (
                  <Download size={14} />
                )}
                <span>{exportLoading ? 'جاري المعالجة...' : 'تصدير'}</span>
                <ChevronDown
                  size={13}
                  className={`chevron-arrow ${isExportMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {isExportMenuOpen && (
                <div className="mindmap-export-menu" role="menu">
                  <button
                    type="button"
                    className="mindmap-export-item"
                    onClick={() => {
                      setIsExportMenuOpen(false);
                      handleExportPng();
                    }}
                    disabled={exportLoading !== null}
                  >
                    <div className="export-item-icon export-png-icon">
                      <ImageIcon size={15} />
                    </div>
                    <div className="export-item-text">
                      <span className="export-item-title">صورة عالية الدقة (PNG)</span>
                      <span className="export-item-desc">بدقة فائقة للمشاركة والحفظ</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    className="mindmap-export-item"
                    onClick={() => {
                      setIsExportMenuOpen(false);
                      handleExportPdf();
                    }}
                    disabled={exportLoading !== null}
                  >
                    <div className="export-item-icon export-pdf-icon">
                      <FileDown size={15} />
                    </div>
                    <div className="export-item-text">
                      <span className="export-item-title">مستند منظم (PDF)</span>
                      <span className="export-item-desc">ملف PDF منسق للطباعة والمراجعة</span>
                    </div>
                  </button>

                  <div className="mindmap-menu-divider" />

                  <button
                    type="button"
                    className="mindmap-export-item"
                    onClick={() => {
                      setIsExportMenuOpen(false);
                      handleExportJson();
                    }}
                  >
                    <div className="export-item-icon export-json-icon">
                      <Download size={15} />
                    </div>
                    <div className="export-item-text">
                      <span className="export-item-title">نسخة احتياطية (JSON)</span>
                      <span className="export-item-desc">حفظ الملف للفتح والتعديل مستقبلاً</span>
                    </div>
                  </button>

                  <label className="mindmap-export-item cursor-pointer">
                    <div className="export-item-icon export-upload-icon">
                      <Upload size={15} />
                    </div>
                    <div className="export-item-text">
                      <span className="export-item-title">استيراد خريطة</span>
                      <span className="export-item-desc">فتح ملف JSON من جهازك</span>
                    </div>
                    <input
                      type="file"
                      accept=".json"
                      className="hidden"
                      onChange={(e) => {
                        setIsExportMenuOpen(false);
                        handleImportJson(e);
                      }}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ============================================================
            2. MAIN CANVAS VIEWPORT (Interactive SVG + DOM Nodes)
            ============================================================ */}
        <div className="mindmap-workspace">
          <div
            ref={canvasContainerRef}
            className={`mindmap-canvas-container ${isPanning ? 'is-panning' : ''}`}
            onMouseDown={handleCanvasPointerDown}
            onMouseMove={handleCanvasPointerMove}
            onMouseUp={handleCanvasPointerUp}
            onDoubleClick={handleCanvasDoubleClick}
            onWheel={handleWheel}
          >
            {/* Empty State Banner when canvas has no nodes */}
            {nodes.length === 0 && (
              <div className="mindmap-empty-state-overlay" onMouseDown={(e) => e.stopPropagation()}>
                <div className="empty-state-card">
                  <div className="empty-state-icon">
                    <Network size={36} />
                  </div>
                  <h3 className="empty-state-title">لوحة خريطة ذهنية فارغة تماماً</h3>
                  <p className="empty-state-desc">
                    صمّم خريطتك الطبية والسريرية يدوياً بحرية كاملة دون أي ارتباط بدروس معينة.
                    انقر نقراً مزدوجاً في أي مكان باللوحة أو اضغط على الزر أدناه:
                  </p>
                  <div className="empty-state-actions">
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => handleAddNodeAt(380, 240, 'card')}
                    >
                      <Plus size={16} />
                      <span>إضافة أول عقدة رئيسية</span>
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setShowTemplatesModal(true)}
                    >
                      <LayoutTemplate size={16} />
                      <span>اختيار نموذج طبي جاهز</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Transformed Stage */}
            <div
              className="mindmap-stage"
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
              }}
            >
              {/* SVG Layer for Arrows and Edges */}
              <svg
                ref={svgRef}
                className="mindmap-svg-layer"
                style={{ width: '4000px', height: '4000px' }}
              >
                <defs>
                  <marker
                    id="arrowhead-default"
                    viewBox="0 0 10 10"
                    refX="9"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto-start-reverse"
                  >
                    <path d="M 0 1 L 10 5 L 0 9 z" fill="#0284c7" />
                  </marker>
                  <marker
                    id="circlehead-default"
                    viewBox="0 0 10 10"
                    refX="5"
                    refY="5"
                    markerWidth="6"
                    markerHeight="6"
                  >
                    <circle cx="5" cy="5" r="4" fill="#0284c7" />
                  </marker>

                  {CLINICAL_COLOR_PALETTES.map((c) => (
                    <React.Fragment key={c.id}>
                      <marker
                        id={`arrow-${c.id}`}
                        viewBox="0 0 10 10"
                        refX="9"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                      >
                        <path d="M 0 1 L 10 5 L 0 9 z" fill={c.hex} />
                      </marker>
                      <marker
                        id={`circle-${c.id}`}
                        viewBox="0 0 10 10"
                        refX="5"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                      >
                        <circle cx="5" cy="5" r="4" fill={c.hex} />
                      </marker>
                    </React.Fragment>
                  ))}
                </defs>

                {/* Render All Edges */}
                {edges.map((edge) => {
                  const { path, midX, midY } = calculateEdgePath(edge);
                  const isSelected = selectedEdgeId === edge.id;
                  const matchingPalette = CLINICAL_COLOR_PALETTES.find((c) => c.hex === edge.color);
                  const markerId = matchingPalette ? matchingPalette.id : 'default';

                  let markerEnd = undefined;
                  let markerStart = undefined;

                  if (edge.arrowHead === 'arrow') {
                    markerEnd = `url(#arrow-${markerId})`;
                  } else if (edge.arrowHead === 'bi-arrow') {
                    markerEnd = `url(#arrow-${markerId})`;
                    markerStart = `url(#arrow-${markerId})`;
                  } else if (edge.arrowHead === 'circle') {
                    markerEnd = `url(#circle-${markerId})`;
                  }

                  let strokeDasharray = undefined;
                  let strokeClass = 'mindmap-edge-path';

                  if (edge.stroke === 'dashed') strokeDasharray = '6,5';
                  if (edge.stroke === 'dotted') strokeDasharray = '3,3';
                  if (edge.stroke === 'pulse') strokeClass += ' edge-pulse';

                  return (
                    <g
                      key={edge.id}
                      className={`mindmap-edge-group ${isSelected ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedEdgeId(edge.id);
                        setSelectedNodeId(null);
                      }}
                    >
                      {/* Transparent wider hit area for easy clicking */}
                      <path
                        d={path}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={20}
                        className="cursor-pointer"
                      />

                      {/* Main visible line */}
                      <path
                        d={path}
                        fill="none"
                        stroke={edge.color || '#0284c7'}
                        strokeWidth={isSelected ? (edge.width || 2.5) + 1.5 : edge.width || 2.5}
                        strokeDasharray={strokeDasharray}
                        markerEnd={markerEnd}
                        markerStart={markerStart}
                        className={strokeClass}
                      />

                      {/* Edge Label (if any) */}
                      {edge.label && edge.label.trim() && (
                        <foreignObject
                          x={midX - 70}
                          y={midY - 14}
                          width={140}
                          height={28}
                          className="overflow-visible pointer-events-none"
                        >
                          <div className="edge-label-pill">
                            <span>{edge.label}</span>
                          </div>
                        </foreignObject>
                      )}
                    </g>
                  );
                })}
              </svg>

              {/* DOM Nodes Layer */}
              {nodes.map((node) => {
                const isSelected = selectedNodeId === node.id;
                const isConnectSource = connectingSourceId === node.id;

                return (
                  <div
                    key={node.id}
                    className={`mindmap-node-card shape-${node.shape} ${
                      isSelected ? 'is-selected' : ''
                    } ${isConnectSource ? 'is-connect-source' : ''}`}
                    style={{
                      left: `${node.x}px`,
                      top: `${node.y}px`,
                      width: node.width ? `${node.width}px` : undefined,
                      borderColor: isSelected ? 'var(--ink)' : node.color,
                    }}
                    onMouseDown={(e) => handleNodeDragStart(e, node)}
                  >
                    {/* Corner Delete Button on Hover */}
                    <button
                      type="button"
                      className="node-corner-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNode(node.id);
                      }}
                      title="مسح هذه العقدة فوراً"
                      aria-label="مسح العقدة"
                    >
                      <Trash2 size={12} />
                    </button>

                    {/* Node Header or Card Top with Color Strip */}
                    <div
                      className="node-color-strip"
                      style={{ backgroundColor: node.color }}
                    >
                      {node.shape === 'card' && (
                        <span className="node-badge-dot" style={{ backgroundColor: '#ffffff' }} />
                      )}
                    </div>

                    {/* Embedded Image (if node has image) */}
                    {node.imageUrl && (
                      <div className="node-image-container">
                        <img
                          src={node.imageUrl}
                          alt={node.title}
                          className="node-embedded-img"
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    )}

                    {/* Node Content Body */}
                    <div className="node-body">
                      {editingNodeTitleId === node.id ? (
                        <input
                          type="text"
                          className="node-title-inline-input"
                          value={node.title}
                          autoFocus
                          onChange={(e) => handleUpdateNode(node.id, { title: e.target.value })}
                          onBlur={() => setEditingNodeTitleId(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') setEditingNodeTitleId(null);
                          }}
                        />
                      ) : (
                        <h4
                          className="node-title"
                          onDoubleClick={() => setEditingNodeTitleId(node.id)}
                          title="انقر مرتين للتحرير السريع"
                          dir="auto"
                        >
                          {node.title}
                        </h4>
                      )}

                      {node.subtitle && <p className="node-subtitle" dir="auto">{node.subtitle}</p>}

                      {node.notes && (
                        <div className="node-notes-box" dir="auto">
                          {node.notes.split('\n').map((line, idx) => (
                            <p key={idx} className="node-notes-line">
                              {line}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Quick Floating Handles on Selected Node */}
                    {isSelected && (
                      <div className="node-floating-actions no-print" onMouseDown={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="node-quick-btn add-child"
                          onClick={() => handleAddChildNode(node)}
                          title="إضافة تفرع فرعي مرتبط"
                        >
                          <Plus size={13} />
                          <span>تفرع</span>
                        </button>

                        <button
                          type="button"
                          className={`node-quick-btn connect ${isConnectSource ? 'active' : ''}`}
                          onClick={() =>
                            setConnectingSourceId(isConnectSource ? null : node.id)
                          }
                          title="ربط بسهم مع عقدة أخرى"
                        >
                          <LinkIcon size={12} />
                          <span>{isConnectSource ? 'اختر الهدف' : 'وصل'}</span>
                        </button>

                        <button
                          type="button"
                          className="node-quick-btn dup"
                          onClick={() => handleDuplicateNode(node)}
                          title="تكرار العقدة"
                        >
                          <Copy size={12} />
                        </button>

                        {/* Direct Delete Button in toolbar */}
                        <button
                          type="button"
                          className="node-quick-btn del"
                          onClick={() => handleDeleteNode(node.id)}
                          title="مسح هذه العقدة (Delete)"
                        >
                          <Trash2 size={13} />
                          <span>حذف</span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ============================================================
              3. RIGHT SIDEBAR / INSPECTOR PANEL (Only when item is selected)
              ============================================================ */}
          {(selectedNode || selectedEdge) && (
            <aside className="mindmap-inspector-dock">
              {/* When a Node is Selected */}
              {selectedNode && (
              <div className="inspector-card">
                <div className="inspector-header">
                  <div className="inspector-title-wrap">
                    <Edit3 size={15} />
                    <span>تخصيص العقدة</span>
                  </div>
                  <button
                    type="button"
                    className="inspector-close-btn"
                    onClick={() => setSelectedNodeId(null)}
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="inspector-content">
                  {/* Title Input */}
                  <div className="inspector-field">
                    <label className="field-label">عنوان العقدة</label>
                    <input
                      type="text"
                      className="inspector-input"
                      value={selectedNode.title}
                      onChange={(e) => handleUpdateNode(selectedNode.id, { title: e.target.value })}
                      placeholder="عنوان المفهوم..."
                      dir="auto"
                    />
                  </div>

                  {/* Subtitle */}
                  <div className="inspector-field">
                    <label className="field-label">الوصف الفرعي (اختياري)</label>
                    <input
                      type="text"
                      className="inspector-input"
                      value={selectedNode.subtitle || ''}
                      onChange={(e) => handleUpdateNode(selectedNode.id, { subtitle: e.target.value })}
                      placeholder="تصنيف، تفصيل، أو ملاحظة..."
                      dir="auto"
                    />
                  </div>

                  {/* Notes */}
                  <div className="inspector-field">
                    <label className="field-label">الملاحظات والتفاصيل</label>
                    <textarea
                      className="inspector-textarea"
                      rows={3}
                      value={selectedNode.notes || ''}
                      onChange={(e) => handleUpdateNode(selectedNode.id, { notes: e.target.value })}
                      placeholder="• نقطة 1&#10;• نقطة 2"
                      dir="auto"
                    />
                  </div>

                  {/* Shape Selector */}
                  <div className="inspector-field">
                    <label className="field-label">{t.mindmapShape}</label>
                    <div className="shape-picker-grid">
                      {(
                        [
                          { id: 'card', name: 'بطاقة' },
                          { id: 'pill', name: 'كبسولة' },
                          { id: 'rounded', name: 'مستطيل' },
                          { id: 'circle', name: 'دائري' },
                          { id: 'hexagon', name: 'معين' },
                        ] as { id: NodeShape; name: string }[]
                      ).map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className={`shape-pick-btn ${selectedNode.shape === s.id ? 'active' : ''}`}
                          onClick={() => handleUpdateNode(selectedNode.id, { shape: s.id })}
                        >
                          <span>{s.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Palette */}
                  <div className="inspector-field">
                    <label className="field-label">{t.mindmapColor}</label>
                    <div className="palette-color-grid">
                      {CLINICAL_COLOR_PALETTES.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={`palette-dot-btn ${selectedNode.color === c.hex ? 'active' : ''}`}
                          style={{ backgroundColor: c.hex }}
                          onClick={() => handleUpdateNode(selectedNode.id, { color: c.hex })}
                          title={language === 'ar' ? c.nameAr : c.nameFr}
                        />
                      ))}
                      <input
                        type="color"
                        className="custom-color-swatch"
                        value={selectedNode.color}
                        onChange={(e) => handleUpdateNode(selectedNode.id, { color: e.target.value })}
                        title="لون مخصص"
                      />
                    </div>
                  </div>

                  {/* Image Attachment (Upload or URL) */}
                  <div className="inspector-field">
                    <label className="field-label">صورة توضيحية للعقدة</label>
                    {selectedNode.imageUrl ? (
                      <div className="image-preview-row">
                        <img
                          src={selectedNode.imageUrl}
                          alt="Node Preview"
                          className="inspector-thumb"
                        />
                        <button
                          type="button"
                          className="btn-danger-subtle btn-xs"
                          onClick={() => handleUpdateNode(selectedNode.id, { imageUrl: undefined })}
                        >
                          <Trash2 size={12} />
                          <span>{t.mindmapRemoveImage}</span>
                        </button>
                      </div>
                    ) : (
                      <div className="image-attach-actions">
                        <label className="btn-secondary btn-sm upload-btn-label">
                          <Upload size={13} />
                          <span>رفع صورة من الجهاز</span>
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              if (f) handleNodeImageUpload(selectedNode.id, f);
                            }}
                          />
                        </label>
                        <input
                          type="url"
                          className="inspector-input text-xs"
                          placeholder="أو الصق رابط صورة (URL)..."
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleUpdateNode(selectedNode.id, {
                                imageUrl: (e.target as HTMLInputElement).value,
                              });
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Actions: Add branch and Clear/Delete this node */}
                  <div className="inspector-actions-row">
                    <button
                      type="button"
                      className="btn-secondary btn-sm flex-1"
                      onClick={() => handleAddChildNode(selectedNode)}
                    >
                      <Plus size={14} />
                      <span>إضافة تفرع</span>
                    </button>
                    <button
                      type="button"
                      className="btn-danger btn-sm"
                      onClick={() => handleDeleteNode(selectedNode.id)}
                      title="مسح هذه العقدة"
                    >
                      <Trash2 size={14} />
                      <span>مسح العقدة</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* When an Edge is Selected */}
            {selectedEdge && !selectedNode && (
              <div className="inspector-card">
                <div className="inspector-header">
                  <div className="inspector-title-wrap">
                    <LinkIcon size={15} />
                    <span>تخصيص السهم والربط</span>
                  </div>
                  <button
                    type="button"
                    className="inspector-close-btn"
                    onClick={() => setSelectedEdgeId(null)}
                  >
                    <X size={14} />
                  </button>
                </div>

                <div className="inspector-content">
                  {/* Edge Label */}
                  <div className="inspector-field">
                    <label className="field-label">نص السهم (العلاقة أو الملاحظة)</label>
                    <input
                      type="text"
                      className="inspector-input"
                      value={selectedEdge.label || ''}
                      onChange={(e) => handleUpdateEdge(selectedEdge.id, { label: e.target.value })}
                      placeholder="مثال: يؤدي إلى، العلاج، التشخيص..."
                      dir="auto"
                    />
                  </div>

                  {/* Connector Curve Style */}
                  <div className="inspector-field">
                    <label className="field-label">انحناء السهم</label>
                    <div className="shape-picker-grid">
                      {(
                        [
                          { id: 'bezier', name: 'منحني ناعم' },
                          { id: 'straight', name: 'مستقيم' },
                          { id: 'step', name: 'مدرج 90°' },
                        ] as { id: ArrowStyle; name: string }[]
                      ).map((st) => (
                        <button
                          key={st.id}
                          type="button"
                          className={`shape-pick-btn ${selectedEdge.style === st.id ? 'active' : ''}`}
                          onClick={() => handleUpdateEdge(selectedEdge.id, { style: st.id })}
                        >
                          <span>{st.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Line Stroke Style */}
                  <div className="inspector-field">
                    <label className="field-label">نمط الخط</label>
                    <div className="shape-picker-grid">
                      {(
                        [
                          { id: 'solid', name: 'متصل' },
                          { id: 'dashed', name: 'متقطع' },
                          { id: 'dotted', name: 'منقط' },
                          { id: 'pulse', name: 'نبض متدفق' },
                        ] as { id: ArrowStroke; name: string }[]
                      ).map((strk) => (
                        <button
                          key={strk.id}
                          type="button"
                          className={`shape-pick-btn ${selectedEdge.stroke === strk.id ? 'active' : ''}`}
                          onClick={() => handleUpdateEdge(selectedEdge.id, { stroke: strk.id })}
                        >
                          <span>{strk.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Arrowhead Style */}
                  <div className="inspector-field">
                    <label className="field-label">رأس السهم</label>
                    <div className="shape-picker-grid">
                      {(
                        [
                          { id: 'arrow', name: 'سهم أحادي' },
                          { id: 'bi-arrow', name: 'سهم ثنائي' },
                          { id: 'circle', name: 'نقطة' },
                          { id: 'none', name: 'خط بسيط' },
                        ] as { id: ArrowHead; name: string }[]
                      ).map((ah) => (
                        <button
                          key={ah.id}
                          type="button"
                          className={`shape-pick-btn ${selectedEdge.arrowHead === ah.id ? 'active' : ''}`}
                          onClick={() => handleUpdateEdge(selectedEdge.id, { arrowHead: ah.id })}
                        >
                          <span>{ah.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Color Palette */}
                  <div className="inspector-field">
                    <label className="field-label">{t.mindmapColor}</label>
                    <div className="palette-color-grid">
                      {CLINICAL_COLOR_PALETTES.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={`palette-dot-btn ${selectedEdge.color === c.hex ? 'active' : ''}`}
                          style={{ backgroundColor: c.hex }}
                          onClick={() => handleUpdateEdge(selectedEdge.id, { color: c.hex })}
                          title={language === 'ar' ? c.nameAr : c.nameFr}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Delete Edge Action */}
                  <div className="inspector-actions-row">
                    <button
                      type="button"
                      className="btn-danger btn-sm w-full"
                      onClick={() => handleDeleteEdge(selectedEdge.id)}
                    >
                      <Trash2 size={14} />
                      <span>{t.mindmapDeleteEdge}</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            </aside>
          )}
        </div>

        {/* ============================================================
            4. TEMPLATES MODAL (Available on demand, not forced)
            ============================================================ */}
        {showTemplatesModal && (
          <div className="mindmap-modal-backdrop" onClick={() => setShowTemplatesModal(false)}>
            <div
              className="mindmap-modal-dialog"
              onClick={(e) => e.stopPropagation()}
              dir={isRtl ? 'rtl' : 'ltr'}
            >
              <div className="mindmap-modal-header">
                <div className="modal-title-wrap">
                  <LayoutTemplate size={18} className="text-primary" />
                  <h3>نماذج الخرائط الذهنية الجاهزة</h3>
                </div>
                <button
                  type="button"
                  className="modal-close-btn"
                  onClick={() => setShowTemplatesModal(false)}
                >
                  <X size={16} />
                </button>
              </div>

              <div className="mindmap-modal-body">
                <p className="modal-intro-text">
                  اختر نموذجاً للبدء به كخريطة جديدة، أو استمر بالخريطة الفارغة للرسم الحر:
                </p>

                <div className="templates-cards-grid">
                  {/* Template 1: Chest Pain */}
                  <div
                    className="template-select-card"
                    onClick={() => handleLoadTemplate('template_chest_pain')}
                  >
                    <div className="template-card-badge red">إسعاف وتشخيص</div>
                    <h4 className="template-card-title">تشخيص ألم الصدر الحاد</h4>
                    <p className="template-card-desc">
                      شجرة متكاملة للتشخيص السريري والتفريقي لألم الصدر: SCA، الانصمام الرئوي، تسلخ الأبهر، والتهاب التامور.
                    </p>
                    <span className="template-card-action">تحميل النموذج ←</span>
                  </div>

                  {/* Template 2: Hypertension Pharmacology */}
                  <div
                    className="template-select-card"
                    onClick={() => handleLoadTemplate('template_pharmaco')}
                  >
                    <div className="template-card-badge blue">علم الأدوية</div>
                    <h4 className="template-card-title">أدوية ضغط الدم (Antihypertenseurs)</h4>
                    <p className="template-card-desc">
                      خريطة تصنيف الأدوية الخافضة للضغط: IEC/ARA II، حاصرات الكالسيوم، المدرات، وحاصرات بيتا.
                    </p>
                    <span className="template-card-action">تحميل النموذج ←</span>
                  </div>

                  {/* Template 3: Clean Blank Map */}
                  <div
                    className="template-select-card blank"
                    onClick={() => {
                      handleCreateNewBlankMap();
                      setShowTemplatesModal(false);
                    }}
                  >
                    <div className="template-card-badge slate">فارغة</div>
                    <h4 className="template-card-title">خريطة فارغة تماماً</h4>
                    <p className="template-card-desc">
                      ابدأ خريطة جديدة تماماً من الصفر وأضف عقدك وأسهمك بحرية.
                    </p>
                    <span className="template-card-action">بدء خريطة بيضاء ←</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MindmapPage;
